// MetricPage.tsx — per-metric graph across commits on a branch.
//
// X axis = ordered commits on the branch (authored_at order); Y axis =
// metric value. Line is colored neutral; regression markers (points that
// got worse vs. previous, given the metric's direction) are tinted red.
//
// The page is reached from the repo page, so it carries the experiment
// through: ?exp=<slug> narrows the series to that experiment's values (the
// same commit can hold an independent measurement under two experiments)
// and every link back selects the commit in that experiment's overview
// pane. Curve metrics have no per-commit series at all — they live on feeds
// — so the page points back at the overview pane for those.
//
// Dirty points (a dirty mark says they are not comparable) are drawn struck
// through — a faded dot with a slash, a struck value in the tooltip and the
// table — with the mark's reason on hover. They never take part in the trend:
// regression markers and deltas compare each clean point against the
// previous clean one.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceDot,
} from 'recharts';
import { unwrapJson } from 'common';
import { apiClient } from '../api';
import type { SeriesResponse } from '../api';
import { buildChartData } from './chartData';
import type { ChartPoint } from './chartData';
import { DIRTY_OPACITY, DIRTY_STYLE, dirtyLabel, dirtyTitle } from './dirty';
import DirtyValue from './DirtyValue';
import { PAGE_REFRESH_MS, useLiveTick } from './live';

export default function MetricPage() {
  const { owner, name, metric } = useParams<{ owner: string; name: string; metric: string }>();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const branchParam = search.get('branch') ?? '';
  const expParam = search.get('exp') ?? '';
  // Back-links and commit-links keep the experiment context.
  const repoHref = `/repos/${owner}/${name}${expParam ? `?exp=${encodeURIComponent(expParam)}` : ''}`;
  const commitHref = (sha: string) =>
    `/repos/${owner}/${name}?${expParam ? `exp=${encodeURIComponent(expParam)}&` : ''}sel=${encodeURIComponent(sha)}`;

  const [branches, setBranches] = useState<string[]>([]);
  const [data, setData] = useState<SeriesResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!owner || !name) return;
    (async () => {
      try {
        const res = await apiClient.fetch(`/api/v1/repos/${owner}/${name}/branches`);
        const body = await unwrapJson<{ branches: string[] }>(res);
        setBranches(body.branches ?? []);
      } catch {
        // non-fatal — page still works without the branch dropdown.
      }
    })();
  }, [owner, name]);

  // Re-read the series once a minute; same-view refreshes are quiet.
  const minute = useLiveTick(true, PAGE_REFRESH_MS);
  const shownKey = useRef('');
  useEffect(() => {
    if (!owner || !name || !metric) return;
    const key = `${owner}/${name}/${metric}/${branchParam}/${expParam}`;
    const quiet = shownKey.current === key;
    if (!quiet) setLoading(true);
    const params = new URLSearchParams();
    if (branchParam) params.set('branch', branchParam);
    if (expParam) params.set('experiment', expParam);
    params.set('limit', '500');
    (async () => {
      try {
        const res = await apiClient.fetch(
          `/api/v1/repos/${owner}/${name}/metrics/${encodeURIComponent(metric)}/series?${params.toString()}`
        );
        const body = await unwrapJson<SeriesResponse>(res);
        setData(body);
        shownKey.current = key;
      } catch (err) {
        if (!quiet) setError(err instanceof Error ? err.message : 'Failed to load series');
      } finally {
        setLoading(false);
      }
    })();
  }, [owner, name, metric, branchParam, expParam, minute]);

  const chartData = useMemo<ChartPoint[]>(() => buildChartData(data), [data]);

  const regressions = useMemo(() => chartData.filter((p) => p.regressed), [chartData]);

  const setBranch = (b: string) => {
    const next = new URLSearchParams(search);
    if (b) next.set('branch', b);
    else next.delete('branch');
    setSearch(next, { replace: true });
  };

  return (
    <main>
      <Link to={repoHref} style={{ opacity: 0.7 }}>← {owner}/{name}</Link>
      <h1 style={{ marginTop: '0.5rem' }}>
        {metric}
        {data?.metric.unit ? (
          <span style={{ opacity: 0.6, fontSize: '0.8em' }}> ({data.metric.unit})</span>
        ) : null}
        <span style={{ opacity: 0.45, marginLeft: '0.5rem', fontSize: '0.7em' }}>
          {data?.metric.direction === 'higher_is_better' ? 'higher is better' : 'lower is better'}
        </span>
      </h1>
      {data?.metric.description && (
        <p style={{ opacity: 0.7 }}>{data.metric.description}</p>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}>
        <label style={{ opacity: 0.7 }}>Branch:</label>
        <select
          className="form-select form-select-sm"
          value={branchParam}
          onChange={(e) => setBranch(e.target.value)}
          style={{ maxWidth: 240 }}
        >
          <option value="">(all)</option>
          {branches.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : data?.metric.kind === 'curve' ? (
        <div className="empty-state">
          <code>{metric}</code> is a curve: it lives on the commits' feeds,
          not on the commits.{' '}
          <Link to={repoHref}>
            Select those commits in the overview pane →
          </Link>
        </div>
      ) : chartData.length === 0 ? (
        <div className="empty-state">
          No data points {branchParam ? `on ${branchParam}` : ''}.
        </div>
      ) : (
        <>
          <div className="chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 24, bottom: 10, left: 8 }}
                onClick={(e) => {
                  // recharts' MouseHandlerDataParam typing omits activePayload,
                  // which the runtime does provide; type-level cast only.
                  const chart = e as { activePayload?: { payload?: ChartPoint }[] } | undefined;
                  const payload = chart?.activePayload?.[0]?.payload;
                  if (payload) {
                    navigate(commitHref(payload.sha));
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis
                  dataKey="short"
                  stroke="rgba(255,255,255,0.5)"
                  fontSize={11}
                  tick={{ fill: 'rgba(255,255,255,0.6)' }}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.5)"
                  fontSize={11}
                  tick={{ fill: 'rgba(255,255,255,0.6)' }}
                />
                <Tooltip
                  contentStyle={{ background: '#23272c', border: '1px solid #444', fontSize: 12 }}
                  labelFormatter={(_, payload) => {
                    const p = payload?.[0]?.payload as ChartPoint | undefined;
                    if (!p) return '';
                    return `${p.short} — ${p.author}`;
                  }}
                  formatter={(value, _key, { payload }) => {
                    const p = payload as ChartPoint;
                    const deltaStr = p.index === 0
                      ? ''
                      : ` (${p.delta >= 0 ? '+' : ''}${p.delta.toFixed(3)})`;
                    if (p.dirty) {
                      return [
                        <span key="v">
                          <span style={DIRTY_STYLE} aria-label={`${value}, ${dirtyLabel(p.dirtyMarks)}`}>
                            {String(value)}
                          </span>
                          {` — ${dirtyLabel(p.dirtyMarks)}`}
                        </span>,
                        p.message,
                      ];
                    }
                    return [`${value}${deltaStr}`, p.message];
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#6ea8d8"
                  strokeWidth={2}
                  dot={(props: { cx?: number; cy?: number; index?: number; payload?: ChartPoint }) => {
                    const { cx = 0, cy = 0, payload } = props;
                    if (!payload?.dirty) {
                      return <circle key={`dot-${props.index}`} cx={cx} cy={cy} r={3} fill="#6ea8d8" />;
                    }
                    // A dirty point: faded and struck with a slash, labelled.
                    return (
                      <g
                        key={`dot-${props.index}`}
                        opacity={DIRTY_OPACITY}
                        role="img"
                        aria-label={`${payload.short} ${payload.value}, ${dirtyLabel(payload.dirtyMarks)}`}
                      >
                        <title>{dirtyTitle(payload.dirtyMarks)}</title>
                        <circle cx={cx} cy={cy} r={4} fill="none" stroke="#6ea8d8" strokeWidth={1.5} />
                        <line x1={cx - 5} y1={cy + 5} x2={cx + 5} y2={cy - 5} stroke="#6ea8d8" strokeWidth={1.5} />
                      </g>
                    );
                  }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
                {regressions.map((p) => (
                  <ReferenceDot
                    key={p.sha}
                    x={p.short}
                    y={p.value}
                    r={5}
                    fill="#e74c3c"
                    stroke="#e74c3c"
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <h2>Data points</h2>
          <table className="table">
            <thead>
              <tr>
                <th>SHA</th>
                <th>Value</th>
                <th>Δ</th>
                <th>Message</th>
                <th>Author</th>
              </tr>
            </thead>
            <tbody>
              {chartData.slice().reverse().map((p) => (
                <tr
                  key={p.sha}
                  style={{
                    ...(p.regressed ? { background: 'rgba(231, 76, 60, 0.08)' } : {}),
                  }}
                  title={dirtyTitle(p.dirtyMarks) || undefined}
                >
                  <td>
                    <Link to={commitHref(p.sha)}>
                      <code>{p.short}</code>
                    </Link>
                  </td>
                  <td>
                    <DirtyValue text={String(p.value)} dirty={p.dirty} marks={p.dirtyMarks} />
                  </td>
                  <td style={{ color: p.regressed ? '#e74c3c' : (p.index === 0 || p.dirty ? 'transparent' : '#7fc97f') }}>
                    {p.index === 0 || p.dirty ? '—' : `${p.delta >= 0 ? '+' : ''}${p.delta.toFixed(3)}`}
                  </td>
                  <td style={{ maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.message}
                  </td>
                  <td style={{ opacity: 0.7 }}>{p.author}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
